import { Component, Input, inject } from '@angular/core';
import { IconModule } from '@shared/icon/icon.module';
import { ToastService } from '../../../core/services/toast/toast.service';

@Component({
  selector: 'app-copyable-text',
  standalone: true,
  imports: [IconModule],
  host: {
    class: 'block min-w-0 max-w-full',
  },
  template: `
    <span [class]="resolvedContainerClass">
      @if (linkHref) {
        <a [href]="linkHref" [class]="resolvedTextClass" [attr.title]="linkTitle">
          {{ displayValue }}
        </a>
      } @else {
        <span [class]="resolvedTextClass">
          {{ displayValue }}
        </span>
      }
      @if (canCopy) {
        <button
          type="button"
          class="inline-flex shrink-0 items-center justify-center rounded p-0.5 text-gray-400 transition-colors hover:text-primary focus:outline-none dark:text-gray-500 dark:hover:text-primary"
          [attr.aria-label]="copyAriaLabel"
          [title]="copyTitle"
          (click)="copy($event)">
          <icon-copy [class]="iconClass" />
        </button>
      }
    </span>
  `,
})
export class CopyableTextComponent {
  private readonly toast = inject(ToastService);

  @Input() value: string | null | undefined;
  @Input() fallback = '—';
  @Input() label = 'Value';
  @Input() textClass = '';
  @Input() containerClass = '';
  @Input() iconClass = 'h-3.5 w-3.5';
  @Input() truncate = true;
  @Input() breakAll = false;
  @Input() linkType: 'email' | 'phone' | null = null;

  get displayValue(): string {
    const raw = (this.value ?? '').trim();
    return raw || this.fallback;
  }

  get canCopy(): boolean {
    return !!(this.value ?? '').trim();
  }

  get linkHref(): string | null {
    const raw = (this.value ?? '').trim();
    if (!raw || raw === '—' || raw === '-') {
      return null;
    }
    if (this.linkType === 'email') {
      return `mailto:${raw}`;
    }
    if (this.linkType === 'phone') {
      return `tel:${raw.replaceAll(/\s+/g, '')}`;
    }
    return null;
  }

  get linkTitle(): string {
    return this.linkType === 'email' ? 'Send email' : 'Call';
  }

  get copyAriaLabel(): string {
    return `Copy ${this.label}`;
  }

  get copyTitle(): string {
    return `Copy ${this.label}`;
  }

  get resolvedContainerClass(): string {
    return [
      'flex w-full min-w-0 max-w-full items-center gap-1',
      this.containerClass,
    ]
      .filter(Boolean)
      .join(' ');
  }

  get resolvedTextClass(): string {
    return [
      'min-w-0 flex-1 overflow-hidden',
      this.breakAll ? 'break-all' : '',
      this.truncate && !this.breakAll ? 'truncate' : '',
      this.textClass,
    ]
      .filter(Boolean)
      .join(' ');
  }

  copy(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    const text = (this.value ?? '').trim();
    if (!text) {
      return;
    }

    navigator.clipboard.writeText(text).then(
      () => this.toast.success(`${this.label} copied to clipboard`),
      () => this.toast.error(`Failed to copy ${this.label.toLowerCase()}`),
    );
  }
}
